import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, FileText, Webhook, Users, Activity, Settings, LogOut, ChevronRight, GraduationCap, Link2, BarChart3, Sparkles, Landmark, IndianRupee, ChevronDown, Wallet, MessageSquare, Network, ShieldCheck, ListChecks, Layers, BookOpen, Target, Flame, PhoneCall } from 'lucide-react';
import NotificationBell from './NotificationBell';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';

const courseQa = {
  label: 'Course Page QA',
  icon: ShieldCheck,
  roles: ['admin', 'manager', 'course_qa'],
  children: [
    { path: '/course-qa', label: 'Audit a Page', icon: ShieldCheck, roles: ['admin', 'manager', 'course_qa'], end: true },
    { path: '/course-qa/all', label: 'All Courses', icon: ListChecks, roles: ['admin', 'manager', 'course_qa'] },
    { path: '/course-qa/defects', label: 'Defect Board', icon: Layers, roles: ['admin', 'manager', 'course_qa'] },
    { path: '/course-qa/rules', label: 'Rules Guide', icon: BookOpen, roles: ['admin', 'manager', 'course_qa'] },
    { path: '/course-qa/management', label: 'Management Report', icon: BarChart3, roles: ['admin', 'manager'] },
  ],
};
const booksAnalytics = {
  label: 'Books Analytics',
  icon: Landmark,
  roles: ['admin', 'manager'],
  children: [
    { path: '/books-analytics', label: 'Overview', icon: BarChart3, roles: ['admin', 'manager'], end: true },
    { path: '/books-analytics/revenue-report', label: 'Revenue Report', icon: IndianRupee, roles: ['admin', 'manager'] },
    { path: '/books-analytics/credit-notes', label: 'Credit Notes', icon: FileText, roles: ['admin', 'manager'] },
    { path: '/books-analytics/sales-conversion', label: 'Sales Conversion', icon: Users, roles: ['admin', 'manager'] },
    { path: '/books-analytics/slip-tracker-audit', label: 'Slip Tracker - Audit', icon: MessageSquare, roles: ['admin', 'manager'] },
  ],
};

const navGroups = [
  { title: 'Overview', items: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'sales_rep', 'viewer'] },
    { path: '/whats-new', label: "What's New", icon: Sparkles, roles: ['admin', 'manager', 'sales_rep', 'viewer'], badgeKey: 'whatsNew' },
    { path: '/activity', label: 'Activity Timeline', icon: Activity, roles: ['admin', 'manager'] },
  ] },
  { title: 'Sales & Incentives', items: [
    { path: '/inside-sales-audit', label: 'Inside Sales Audit', icon: Target, roles: ['admin', 'manager'] },
    { path: '/hot-lead-transfer', label: 'Hot Lead Transfer', icon: Flame, roles: ['admin', 'manager'] },
    { path: '/callback-tracker', label: 'Call Back Follow-up', icon: PhoneCall, roles: ['admin', 'manager'] },
    { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'manager'] },
  ] },
  { title: 'Training', items: [
    { path: '/training', label: 'Training Mgmt', icon: GraduationCap, roles: ['admin', 'manager'] },
    courseQa,
  ] },
  { title: 'Finance', items: [
    { path: '/cash-register', label: 'Cash Register', icon: Wallet, roles: ['admin', 'manager'] },
    booksAnalytics,
  ] },
  { title: 'Zoho & Data', items: [
    { path: '/submissions', label: 'Submissions', icon: FileText, roles: ['admin', 'manager', 'sales_rep', 'viewer'] },
    { path: '/webhooks', label: 'Webhooks', icon: Webhook, roles: ['admin', 'manager'] },
    { path: '/zoho-reports', label: 'Zoho Reports', icon: Link2, roles: ['admin', 'manager'] },
    { path: '/zoho-integration-map', label: 'Zoho Integration Map', icon: Network, roles: ['admin', 'manager'] },
  ] },
  { title: 'Admin', items: [
    { path: '/users', label: 'Users', icon: Users, roles: ['admin', 'manager'] },
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin'] },
  ] },
];
const navItems = navGroups.flatMap(g => g.items);

const roleColors = {
  admin: 'bg-red-100 text-red-700',
  manager: 'bg-blue-100 text-blue-700',
  sales_rep: 'bg-green-100 text-green-700',
  restricted_user: 'bg-purple-100 text-purple-700',
  viewer: 'bg-gray-100 text-gray-600',
};

const slug = l => l.toLowerCase().replace(/\s/g, '-');
const rowCls = (active, sub) => `group relative flex items-center gap-2.5 px-2.5 ${sub ? 'py-1.5 text-[12.5px]' : 'py-[7px] text-[13px]'} rounded-md font-medium transition-colors duration-150 ${
  active ? 'bg-slate-100 text-slate-900 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-slate-900'
    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`;

const NavItem = ({ item, badge }) => (
  <NavLink to={item.path} end={item.end} data-testid={`nav-${slug(item.label)}`} className={({ isActive }) => rowCls(isActive, false)}>
    {({ isActive }) => (
      <>
        <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-700'}`} />
        <span className="flex-1 truncate">{item.label}</span>
        {badge > 0 && (
          <span data-testid={item.path === '/submissions' ? 'nav-pending-deletions-badge' : 'nav-whats-new-badge'}
            className={`ml-auto text-white text-[10px] font-bold px-1.5 rounded-full min-w-[18px] text-center leading-[16px] ${item.path === '/submissions' ? 'bg-amber-500' : 'bg-violet-500'}`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </>
    )}
  </NavLink>
);

const NavGroup = ({ item, open, onToggle, pathname }) => {
  const active = item.children.some(c => pathname === c.path || pathname.startsWith(c.path + '/'));
  return (
    <div>
      <button type="button" onClick={onToggle} data-testid={`nav-group-${slug(item.label)}`}
        className={`w-full group flex items-center gap-2.5 px-2.5 py-[7px] rounded-md text-[13px] font-medium transition-colors duration-150 ${active ? 'text-slate-900' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}>
        <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-700'}`} />
        <span className="flex-1 text-left truncate">{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="ml-[18px] mt-0.5 mb-1 space-y-0.5 border-l border-slate-200 pl-2">
          {item.children.map(c => (
            <NavLink key={c.path} to={c.path} end={c.end} data-testid={`nav-${slug(c.label)}`} className={({ isActive }) => rowCls(isActive, true)}>
              <span className="flex-1 truncate">{c.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const visibleGroups = navGroups
    .map(g => ({ ...g, items: g.items
      .filter(item => item.roles.includes(user?.role))
      .map(item => item.children ? { ...item, children: item.children.filter(c => c.roles.includes(user?.role)) } : item) }))
    .filter(g => g.items.length);
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  const [whatsNewUnread, setWhatsNewUnread] = useState(0);
  const [openGroups, setOpenGroups] = useState(() => {
    // Auto-open any group whose child matches the current path on mount
    const init = {};
    navItems.forEach(it => {
      if (it.children && it.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'))) init[it.label] = true;
    });
    return init;
  });
  const toggleGroup = (label) => setOpenGroups(g => ({ ...g, [label]: !g[label] }));

  const refreshWhatsNew = useCallback(async () => {
    if (user?.role === 'course_qa') return;
    try {
      const res = await api.get('/whats-new/unread-count');
      setWhatsNewUnread(res.data.unread || 0);
    } catch {}
  }, []);
  const [pendingDeletions, setPendingDeletions] = useState(0);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const load = () => api.get('/deletion-requests/pending-ids').then(r => setPendingDeletions(r.data.pending_count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user?.role]);
  useEffect(() => {
    refreshWhatsNew();
    const onRead = () => refreshWhatsNew();
    window.addEventListener('whats-new-read', onRead);
    return () => window.removeEventListener('whats-new-read', onRead);
  }, [refreshWhatsNew]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-60 flex-shrink-0 border-r border-border bg-white flex flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center px-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <LayoutDashboard className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-base tracking-tight">Command Center</span>
          </div>
        </div>

        <ScrollArea className="flex-1 py-3">
          <nav className="px-3" data-testid="sidebar-nav">
            {visibleGroups.map((g, gi) => (
              <div key={g.title} className={gi ? 'mt-4' : ''} data-testid={`nav-section-${g.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <p className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{g.title}</p>
                <div className="space-y-0.5">
                  {g.items.map(item => item.children
                    ? <NavGroup key={item.label} item={item} open={!!openGroups[item.label]} onToggle={() => toggleGroup(item.label)} pathname={location.pathname} />
                    : <NavItem key={item.path} item={item} badge={item.path === '/submissions' ? pendingDeletions : item.badgeKey === 'whatsNew' ? whatsNewUnread : 0} />)}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>

        <div className="border-t border-border p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full text-left hover:bg-secondary rounded-sm p-2 transition-colors duration-150" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8 rounded-sm">
                  <AvatarFallback className="rounded-sm bg-primary text-primary-foreground text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.full_name}</p>
                  <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${roleColors[user?.role] || ''}`}>
                    {user?.role === 'course_qa' ? 'Course QA Reviewer' : user?.role?.replace('_', ' ')}
                  </Badge>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs text-muted-foreground" disabled>{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-600">
                <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 glass-header border-b border-border flex items-center justify-between px-8 sticky top-0 z-40" data-testid="main-header">
          <div />
          <div className="flex items-center gap-4">
            <span className="text-xs font-data text-muted-foreground">{new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
            {user?.role !== 'course_qa' && <NotificationBell />}
          </div>
        </header>
        <ScrollArea className="flex-1">
          <main className="p-6 md:p-8 lg:p-10 max-w-[1600px]">
            <Outlet />
          </main>
        </ScrollArea>
      </div>
    </div>
  );
}
